/**
 * useStableWindowDimensions — useWindowDimensions, except that while a soft
 * keyboard is open the HEIGHT stays at its pre-keyboard value.
 *
 * Android resizes the window for the keyboard (native `adjustResize`, and
 * Chrome's `interactive-widget=resizes-content`), so anything sized from the
 * window height — the game map — shrank and the map controls bar rode up the
 * moment the chat input was focused. The map should hold still; the chat input
 * lifts over it instead (GameMapHost).
 *
 * Width / orientation changes always pass through (a rotation is never
 * mistaken for a keyboard). Native-safe: web globals are read behind guards.
 */
import { useEffect, useRef, useState } from 'react'
import { Keyboard, Platform, useWindowDimensions } from 'react-native'
import { isCoarsePointerWeb } from '../lib/platform'
import { stableWindowHeight } from '../lib/stableWindow'

function webEditableFocused(): boolean {
  if (Platform.OS !== 'web' || typeof document === 'undefined') return false
  const el = document.activeElement as HTMLElement | null
  if (!el) return false
  return el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable
}

export function useStableWindowDimensions() {
  const dims = useWindowDimensions()
  const [nativeKeyboardUp, setNativeKeyboardUp] = useState(false)
  const settled = useRef({ width: dims.width, height: dims.height })

  useEffect(() => {
    if (Platform.OS === 'web') return
    const show = Keyboard.addListener(Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow', () => setNativeKeyboardUp(true))
    const hide = Keyboard.addListener(Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide', () => setNativeKeyboardUp(false))
    return () => {
      show.remove()
      hide.remove()
    }
  }, [])

  const keyboardUp = nativeKeyboardUp || (isCoarsePointerWeb() && webEditableFocused())
  const height = stableWindowHeight({
    width: dims.width,
    height: dims.height,
    keyboardUp,
    settled: settled.current,
  })
  if (!keyboardUp || dims.width !== settled.current.width) {
    settled.current = { width: dims.width, height: dims.height }
  }
  return { ...dims, height }
}
